import formatIntlPhoneNumber from 'data/services/phone/formatter';
import {
    formatCustomValue,
    getBillToCustomValue,
    visibleBillToFields,
} from '../helpers';
import { Invoice, InvoiceAcceptance } from '../types';

const normalizeLabel = (label: string) => (
    label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
);

const NOMBRE = 'NOMBREDELDESTINATARIO';
const TELEFONO = 'TELEFONODELDESTINATARIO';
const CI = 'CIDELDESTINATARIO';
const AUTO = 'AUTOMARCAYMODELO';
const YEAR = 'ANODELAUTO';
const COLOR = 'COLOR';
const VIN = 'VIN';
const SENDER_NAME = ['NOMBREDELREMITENTE', 'NOMBRE'];
const SENDER_PHONE = ['TELEFONODELREMITENTE', 'TELEFONO'];

export interface PrestigeStickerQrPayload {
    organizationId: string;
    invoiceId: string;
    vin: string;
    car: {
        makeModel: string;
        year: string;
        color: string;
    };
    receiver: {
        name: string;
        phone: string;
        ci: string;
    };
    sender: {
        customerId: string;
        name: string;
        phone: string;
    };
}

export interface PrestigeStickerModel {
    logoImageUrl: string;
    wordmark: string;
    nombre: string;
    telefono: string;
    auto: string;
    vin: string;
    contactPhone: string;
    footerImages: { id: string; imageUrl: string }[];
    isVoid: boolean;
}

const fieldValue = (invoice: Invoice, labelKey: string) => {
    const match = visibleBillToFields(invoice.document.design.billToFields)
        .find((field) => normalizeLabel(field.label) === labelKey);
    if (!match) return '';
    return formatCustomValue(
        getBillToCustomValue(invoice.document.billToCustomValues, match.id),
        match.valueKind,
    ).trim().toUpperCase();
};

const firstFieldValue = (invoice: Invoice, labelKeys: string[]) => {
    const match = labelKeys
        .map((labelKey) => fieldValue(invoice, labelKey))
        .find((value) => value);
    return match || '';
};

export const joinStickerAuto = (marca: string, year: string) => {
    const model = marca.trim().toUpperCase();
    const ano = year.trim().toUpperCase();
    if (!ano) return model;
    if (!model) return ano;
    if (model.endsWith(ano)) return model;
    return `${model} ${ano}`;
};

const contactPhone = (invoice: Invoice) => {
    const raw = (
        invoice.document.design.seller.contact?.phone
        || invoice.document.seller.contact?.phone
        || ''
    ).trim();
    if (!raw) return '';
    return formatIntlPhoneNumber(raw) || raw;
};

export const getPrestigeStickerQrPayload = (
    invoice: Invoice,
    organizationId: string,
): PrestigeStickerQrPayload => ({
    organizationId,
    invoiceId: invoice.id,
    vin: fieldValue(invoice, VIN),
    car: {
        makeModel: fieldValue(invoice, AUTO),
        year: fieldValue(invoice, YEAR),
        color: fieldValue(invoice, COLOR),
    },
    receiver: {
        name: fieldValue(invoice, NOMBRE),
        phone: fieldValue(invoice, TELEFONO),
        ci: fieldValue(invoice, CI),
    },
    sender: {
        customerId: invoice.customer.id,
        name: firstFieldValue(invoice, SENDER_NAME),
        phone: firstFieldValue(invoice, SENDER_PHONE),
    },
});

const bytesToBinary = (bytes: Uint8Array) => {
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return binary;
};

const utf8Bytes = (value: string) => {
    const encoded = unescape(encodeURIComponent(value));
    const bytes = new Uint8Array(encoded.length);
    for (let index = 0; index < encoded.length; index += 1) {
        bytes[index] = encoded.charCodeAt(index);
    }
    return bytes;
};

export const encodeStickerQrValue = (payload: PrestigeStickerQrPayload) => (
    btoa(bytesToBinary(utf8Bytes(JSON.stringify(payload))))
);

export const decodeStickerQrValue = (value: string): PrestigeStickerQrPayload => (
    JSON.parse(decodeURIComponent(escape(atob(value))))
);

export const stickerPdfAuthor = (
    invoice: Invoice,
    acceptance: InvoiceAcceptance | null = null,
) => (
    invoice.document.design.seller.tradingName
    || invoice.document.design.seller.legalName
    || acceptance?.name
    || ''
);

export const getPrestigeStickerModel = (invoice: Invoice): PrestigeStickerModel => {
    const { design } = invoice.document;
    const footerBlocks = [...(design.footerBlocks || [])]
        .sort((left, right) => left.order - right.order);
    return {
        logoImageUrl: design.logo.style === 'image' ? design.logo.imageUrl : '',
        wordmark: design.logo.style === 'wordmark' ? design.logo.text : '',
        nombre: fieldValue(invoice, NOMBRE),
        telefono: fieldValue(invoice, TELEFONO),
        auto: joinStickerAuto(fieldValue(invoice, AUTO), fieldValue(invoice, YEAR)),
        vin: fieldValue(invoice, VIN),
        contactPhone: contactPhone(invoice),
        footerImages: footerBlocks.flatMap((block) => (
            block.kind === 'image' && block.imageUrl
                ? [{ id: block.id, imageUrl: block.imageUrl }]
                : []
        )),
        isVoid: invoice.status === 'void',
    };
};
